import { TbX } from "solid-icons/tb";
import { type Component, For, createEffect, createSignal } from "solid-js";

interface TagInputProps {
  placeholder: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  onAdd?: () => void;
  onInputChange?: (inputValue: string) => void;
  validator?: (value: string) => boolean;
  class?: string;
}

export const TagInput: Component<TagInputProps> = (props) => {
  const [inputValue, setInputValue] = createSignal("");
  const [tags, setTags] = createSignal<string[]>(props.value || []);

  // Sync with external value changes
  createEffect(() => {
    setTags(props.value || []);
  });

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Check for duplicates
    if (tags().includes(trimmed)) {
      setInputValue("");
      return;
    }

    // Validate if validator is provided
    if (props.validator && !props.validator(trimmed)) {
      // Invalid value - clear input but don't add tag
      setInputValue("");
      return;
    }

    // Add valid tag
    const newTags = [...tags(), trimmed];
    setTags(newTags);
    props.onValueChange(newTags);
    setInputValue("");
  };

  const removeTag = (index: number) => {
    const newTags = tags().filter((_, i) => i !== index);
    setTags(newTags);
    props.onValueChange(newTags);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    const target = e.currentTarget as HTMLInputElement;
    const value = target.value;

    if (e.key === "Enter") {
      e.preventDefault();
      addTag(value);
      if (props.onAdd) {
        props.onAdd();
      }
    } else if (e.key === " " || e.key === ",") {
      e.preventDefault();
      addTag(value);
    } else if (e.key === "Backspace" && value === "" && tags().length > 0) {
      e.preventDefault();
      removeTag(tags().length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData("text") || "";
    const values = pastedText.split(/[\s,;]+/).filter((v) => v.trim());

    values.forEach((value) => {
      addTag(value);
    });
  };

  const handleBlur = () => {
    const value = inputValue().trim();
    if (value) {
      addTag(value);
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      class={`flex flex-wrap gap-2 px-3 py-2 bg-canvas border border-primary-border rounded-lg focus-within:ring-2 focus-within:ring-brand ${props.class || ""}`}
      onClick={(e) => {
        const input = e.currentTarget.querySelector("input");
        if (input && e.target === e.currentTarget) {
          input.focus();
        }
      }}
    >
      <For each={tags()}>
        {(tag, index) => (
          <div class="flex items-center gap-1 px-2 py-1 bg-brand/10 text-brand rounded text-sm">
            <span>{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(index())}
              class="hover:bg-brand/20 rounded p-0.5 transition-colors"
            >
              <TbX class="w-3 h-3" />
            </button>
          </div>
        )}
      </For>
      <input
        type="text"
        value={inputValue()}
        onInput={(e) => {
          const value = e.currentTarget.value;
          setInputValue(value);
          props.onInputChange?.(value);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={tags().length === 0 ? props.placeholder : ""}
        class="flex-1 min-w-[120px] bg-transparent outline-none text-sm"
      />
    </div>
  );
};
